import store from '@/store';
import ErrorMessage from './ErrorMessage';
import 'cross-fetch/polyfill';

const defaultOptions = {
  headers: {
    'Cache-Control': 'no-cache'
  }
};

const defaultPostOptions = {
  ...defaultOptions,
  method: 'POST',
  headers: {
    ...defaultOptions.headers,
    'Content-Type': 'application/json',
  },
  cache: 'no-store',
};

const defaultGetOptions = {
  ...defaultOptions,
  method: 'GET',
  headers: {
    ...defaultOptions.headers
  }
};

export default class Requests {
  /**
   * Performs a GET request and returns JSON response
   * @param {string} url - The URL to fetch
   * @param {Object} options - Fetch options
   * @returns {Promise<Object>} - JSON response
   */
  static async get(url, options = defaultGetOptions) {
    try {
      const res = await fetch(url, options);

      if (!res.ok) {
        throw new Error(`Bad response from server: ${res.status}`);
      }

      const response = await res.json();
      // Nur den UMFANG melden, nicht die Nutzlast.
      //
      // Vorher stand hier die komplette Antwort. Menue und Kanalliste sind
      // jeweils ueber 50 KB JSON, und der Live-Log des Admins spiegelt
      // console.* (store/modules/socket.js): ein einziger Seitenaufruf hat
      // damit den gesamten sichtbaren Verlauf verdraengt. Bei der Suche nach
      // einem Cast- oder Standby-Problem blieben von 212 Eintraegen genau zwei
      // brauchbare uebrig - der Rest war diese eine Zeile.
      //
      // Was man zur Fehlersuche wirklich braucht, ist die Adresse und ob etwas
      // zurueckkam. Die Daten selbst stehen in der Datenbank.
      console.log(
        '[Request] GET ' + url + ' -> ' +
        (Array.isArray(response)
          ? response.length + ' Eintraege'
          : typeof response === 'object' && response !== null
            ? Object.keys(response).length + ' Felder'
            : String(response))
      );
      return response;
    } catch (err) {
      console.error('GET request failed:', err);
      return Promise.reject(err);
    }
  }

  /**
   * Performs a GET request and returns XML response
   * @param {string} url - The URL to fetch
   * @param {Object} options - Fetch options
   * @returns {Promise<Document>} - XML document
   */
  static async getXml(url, options = defaultGetOptions) {
    try {
      const res = await fetch(url, options);

      if (!res.ok) {
        throw new Error(`Bad response from server: ${res.status}`);
      }

      const text = await res.text();
      return new window.DOMParser().parseFromString(text, "text/xml");
    } catch (err) {
      console.error('XML request failed:', err);
      return Promise.reject(err);
    }
  }

  /**
   * Performs a POST request
   * @param {string} url - The URL to fetch
   * @param {Object} data - The data to send
   * @param {Object} optionParam - Custom fetch options
   * @returns {Promise<Object>} - JSON response
   */
  static async post(url = '', data = {}, optionParam) {
    try {
      const options = optionParam || { ...defaultPostOptions };

      if (!optionParam) {
        options.body = JSON.stringify(data);
      }

      const response = await fetch(url, options);

      if (!response.ok) {
        throw new Error(`Bad response from server: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      console.error('POST request failed:', url, err);
      return Promise.reject(err);
    }
  }

  /**
   * Fetches custom design for a building
   * @param {number} bid - Building ID
   * @returns {Promise<Object>} - Custom design data
   */
  static async fetchCustomDesign(bid = 2) {
    const response = await this.get(
      `${process.env.API_PORTAL}/bid/${bid}/customizer`
    );
    return response[0];
  }

  /**
   * Fetches building ID, tries POST if GET fails
   * @returns {Promise<Object>} - Building ID data
   */
  static async fetchBuildingId() {
    try {
      const response = await this.get(`${process.env.API_PORTAL}/getbid/`);
      console.log('RESPONSE GET', response);

      if (response && response.length > 0) {
        return response[0];
      }

      // If GET returns empty array, try POST
      const responsePost = await this.post(`${process.env.API_PORTAL}/getbid/`);
      console.log('RESPONSE POST', responsePost);
      return responsePost;
    } catch (err) {
      console.error('Error fetching building ID:', err);
      return Promise.reject(err);
    }
  }

  /**
   * Fetches building details
   * @param {number|string} bid - Building ID
   * @returns {Promise<Object>} - Building details
   */
  static async fetchBuildingDetails(bid) {
    try {
      return await this.get(
        `${process.env.API_FIAS}/getBuildingDetails/${bid}`
      );
    } catch (err) {
      console.error('Could not fetch data for building details:', bid, err);
      return {};
    }
  }

  /**
   * Sucht ein Geraet anhand seines Fingerprints.
   * Gibt null zurueck wenn nicht gefunden oder API kein Fingerprint-Lookup unterstuetzt.
   * @param {string} fingerprint - SHA-256 Fingerprint des Geraets
   * @returns {Promise<Object|null>} - Geraetedaten oder null
   */
  static async fetchDeviceByFingerprint(fingerprint, device) {
    console.log('=======================================[Fingerprint] lookup:', fingerprint);
    //https://stagingapi.digmedia.net/api/v1/getbid/devices/?fingerprint=6327d6c991625dc26327d6c991625dc2000000000000002a6327d6c991625dc2&serialNumber=211KKYUKJ362&mac=7c:64:6c:04:58:d7
    try {
      const result = await this.get(
        `${process.env.API_PORTAL}/getbid/devices/?fingerprint=${encodeURIComponent(fingerprint)}&serialNumber=${encodeURIComponent(device.serialNumber)}&mac=${encodeURIComponent(device.mac)}`
      );
      // API kann Array oder einzelnes Objekt zurueckgeben
      if (Array.isArray(result)) return result.length > 0 ? result[0] : null;
      return result && result.id ? result : null;
    } catch (err) {
      // Endpoint nicht vorhanden oder kein Ergebnis → null zurueck (kein Abbruch)
      console.log('[Fingerprint] lookup not available or no result:', err.message);
      return null;
    }
  }

  /**
   * Sucht ein Geraet ueber Seriennummer und MAC - der Fallback, wenn der
   * Fingerprint nichts findet.
   *
   * Warum es ihn braucht: der Fingerprint steht erst seit 03/2026 ueberhaupt in
   * der DB, und bei Altgeraeten wird er nur nachgetragen, solange das Feld dort
   * leer ist. Wird der Speicher eines solchen Geraets geleert, ist die einzige
   * Kopie weg - ueber den Fingerprint faende es sich nie wieder und landete in
   * der Registrierung, obwohl sein Datensatz laengst existiert. Seriennummer
   * und MAC dagegen stehen bei jedem Geraet in der DB und kommen aus der
   * Firmware, nicht aus dem App-Speicher.
   *
   * WARUM DIE MAC MITMUSS, obwohl hier die Seriennummer sucht: der Endpunkt
   * (mongorestapi, getBid.js -> getDeviceProps) beantwortet die Abfrage nur mit
   * serialNumber (min. 6 Zeichen) UND mac (min. 12 Zeichen), sonst 403. Das ist
   * der Schutz davor, dass sich ueber diesen oeffentlichen Endpunkt die
   * Geraeteliste abziehen laesst. Alle Query-Parameter werden dort per
   * query-to-mongo zu einem UND verknuepft - der Unterschied zur
   * Fingerprint-Abfrage ist also genau der weggelassene Fingerprint.
   *
   * Die Antwort wird NICHT blind uebernommen: es zaehlt nur ein Datensatz,
   * dessen serialNumber wirklich gleich ist. Ignorierte die API den Filter und
   * lieferte die ganze Liste, uebernaehme das TV sonst ein fremdes Geraet -
   * mit fremdem Building und fremdem Zimmer.
   *
   * @param {string} serialNumber - Seriennummer des Geraets
   * @param {string} mac - MAC des Geraets (vom Endpunkt verlangt)
   * @returns {Promise<Object|null>} - Geraetedaten oder null
   */
  static async fetchDeviceBySerialNumber(serialNumber, mac) {
    const wanted = String(serialNumber || '').trim().toUpperCase();
    const macValue = String(mac || '').trim();
    if (wanted.length < 6) return null;
    // Ohne MAC antwortet der Endpunkt mit 403 - den Aufruf sparen wir uns.
    if (macValue.length < 12) {
      console.log('[Fingerprint] Fallback ohne MAC nicht moeglich - der Endpunkt verlangt sie');
      return null;
    }
    console.log('=======================================[Fingerprint] Fallback ueber Seriennummer:', serialNumber);
    try {
      const result = await this.get(
        `${process.env.API_PORTAL}/getbid/devices/?serialNumber=${encodeURIComponent(serialNumber)}&mac=${encodeURIComponent(macValue)}`
      );
      const list = Array.isArray(result) ? result : [result];
      const matches = list.filter(
        (item) =>
          item &&
          item.id &&
          String(item.serialNumber || '').trim().toUpperCase() === wanted
      );
      if (!matches.length) return null;
      if (matches.length === 1) return matches[0];
      // Mehrere Eintraege zur selben Seriennummer: Doppelanlagen aus der Zeit,
      // als ein Lesefehler einen falschen Fingerprint festschreiben konnte. Der
      // zuletzt aktualisierte ist der, mit dem das Geraet zuletzt gelaufen ist -
      // die anderen sind Karteileichen. Irgendeinen zu nehmen hiesse, das TV mit
      // einer Wahrscheinlichkeit ins falsche Zimmer zu haengen.
      console.log('[Fingerprint] ' + matches.length + ' Eintraege zur Seriennummer - der juengste gewinnt');
      return matches.reduce((best, item) =>
        Number(item.updated || 0) > Number(best.updated || 0) ? item : best
      );
    } catch (err) {
      console.log('[Fingerprint] Seriennummer-Lookup nicht moeglich:', err.message);
      return null;
    }
  }

  /**
   * Creates a new device
   * @param {Object} device - Device data
   * @returns {Promise<Object>} - Created device data
   */
  static async postCreateDevice(device) {
    try {
      const response = await this.post(
        `${process.env.API_PORTAL}/getbid/devices/`,
        device
      );

      store.dispatch('notification/new', {
        title: 'Saved',
        message: `DeviceId: ${JSON.stringify(response)}`,
      });

      console.log(response);
      return response;
    } catch (err) {
      console.error('Failed post device:', device, err);
      store.dispatch('notification/new', {
        title: 'Failed post device',
        message: `DeviceId: ${JSON.stringify(device)}, Err: ${err}`,
      });
      return Promise.reject(err);
    }
  }

  /**
   * Updates device data (IP, MAC, resWidth, resHeight, Fingerprint, Version, Firmware etc.)
   * DeviceId, buildingId and room are NOT updated.
   * @param {Object} params - Parameters
   * @param {number|string} params.bid - Building ID
   * @param {string} params.deviceId - Device ID
   * @param {Object} params.data - Data fields to update
   * @returns {Promise<Object>} - Updated device data
   */
  static async updateDeviceInfo({ bid, deviceId, data }) {
    if (bid === undefined || deviceId === undefined) {
      return ErrorMessage.missingParameter({ bid, deviceId });
    }

    try {
      const response = await fetch(
        `${process.env.API_PORTAL}/bid/${bid}/devices/${deviceId}`,
        {
          ...defaultPostOptions,
          //method: 'PUT',
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        throw new Error(`Bad response from server: ${response.status}`);
      }

      const result = await response.json();
      store.dispatch('notification/new', {
        title: 'Device aktualisiert',
        message: `IP: ${data.ip}, MAC: ${data.mac}`,
      });
      return result;
    } catch (err) {
      console.error('Failed to update device:', err);
      store.dispatch('notification/new', {
        title: 'Update fehlgeschlagen',
        message: String(err),
      });
      return Promise.reject(err);
    }
  }

  /**
   * Fetches device information
   * @param {Object} params - Parameters
   * @param {number|string} params.bid - Building ID
   * @param {string} params.deviceId - Device ID
   * @returns {Promise<Object>} - Device information
   */
  static async fetchDeviceInfo({ bid, deviceId }) {
    if (bid === undefined || deviceId === undefined) {
      return ErrorMessage.missingParameter({ bid, deviceId });
    }

    try {
      return await this.get(
        `${process.env.API_PORTAL}/bid/${bid}/devices/${deviceId}`
      );
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * Fetches VOD categories
   * @param {Object} params - Parameters
   * @param {number|string} params.bid - Building ID
   * @returns {Promise<Object>} - VOD categories
   */
  static async fetchVodCategory({ bid }) {
    try {
      return await this.get(
        `${process.env.API_PORTAL}/bid/${bid}/vod-genres/`
      );
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * Fetches VOD content
   * @param {Object} params - Parameters
   * @param {number|string} params.bid - Building ID
   * @returns {Promise<Object>} - VOD content
   */
  static async fetchVod({ bid }) {
    try {
      return await this.get(
        `${process.env.API_PORTAL}/bid/${bid}/vod/`
      );
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * Fetches channel list
   * @param {Object} params - Parameters
   * @param {number|string} params.bid - Building ID
   * @returns {Promise<Object>} - Channel list
   */
  static async fetchChannelList({ bid }) {
    try {
      return await this.get(
        `${process.env.API_PORTAL}/bid/${bid}/channels/`
      );
    } catch (err) {
      return Promise.reject(err);
    }
  }

  static async fetchEpgByChannels({channels}) {
    if (channels === undefined) {
      return ErrorMessage.missingParameter({ channels });
    }

    try {
      const from = Math.round(+new Date() / 1000);

      // Parallel statt sequentiell: alle Kanaele gleichzeitig abholen.
      // Die alte for/await-Schleife verlaengerte den Home-Load linear mit
      // der Kanalanzahl und blockierte den Main-Thread indirekt, weil sich
      // die XML-Parse-Bursts aneinanderreihten.
      // Promise.allSettled ist in core-js/babel-runtime der TV-Bundle-Toolchain
      // nicht verfuegbar (alte LG WebOS / Chrome 38). Wir wrappen die Fetches
      // manuell, sodass ein einzelner toter Kanal den Pool nicht kippt.
      const wrapped = channels.map((channel) =>
        this.getXml(
          `${process.env.EPG_API}/epg?channel_id=${channel.epgChannelId}&number=110&from=${from}`
        ).then(
          (value) => ({ ok: true, value }),
          (reason) => {
            console.warn('EPG channel fetch failed:', channel && channel.epgChannelId, reason);
            return { ok: false };
          }
        )
      );
      const results = await Promise.all(wrapped);
      return results.filter((r) => r.ok && r.value).map((r) => r.value);
    } catch (err) {
      console.error('Failed to fetch EPG by channels:', err);
      return Promise.reject(err);
    }
  }

  /**
   * Fetches TV packages for a device
   * @param {Object} params - Parameters
   * @param {string} params.deviceId - Device ID
   * @returns {Promise<Object>} - TV packages
   */
  /**
   * Fetches the EPG of a SINGLE channel (TV-Guide v2).
   *
   * Absichtlich einzeln statt gebuendelt wie fetchEpgByChannels: nur so laesst
   * sich die Anzahl gleichzeitiger Requests drosseln. fetchEpgByChannels feuert
   * alle Kanaele parallel los, was bei 60+ Sendern Netz und TV kurzzeitig
   * saettigt und die Oberflaeche blockiert.
   *
   * @param {Object} params
   * @param {number|string} params.epgChannelId
   * @param {number} [params.from] - Unix-Sekunden; Default jetzt
   * @param {number} [params.number] - Anzahl Events (48h ~ 70 Events)
   * @returns {Promise<Document>} XML-Dokument
   */
  static async fetchEpgForChannel({ epgChannelId, from, number = 110 }) {
    if (epgChannelId === undefined || epgChannelId === null) {
      return ErrorMessage.missingParameter({ epgChannelId });
    }
    const fromSec = from === undefined ? Math.round(+new Date() / 1000) : from;
    return this.getXml(
      `${process.env.EPG_API}/epg?channel_id=${epgChannelId}&number=${number}&from=${fromSec}`
    );
  }

  static async fetchTvPackages({deviceId}) {
    if (deviceId === undefined) {
      return ErrorMessage.missingParameter({ deviceId });
    }

    try {
      return await this.get(
        `${process.env.TV_PACKAGES}/packages/devices/${deviceId}`
      );
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * Fetches submenu for a widget
   * @param {number|string} id - Widget ID
   * @returns {Promise<Object>} - Submenu data
   */
  static async fetchSubMenu(id) {
    try {
      return await this.get(
        `${process.env.API_PORTAL}/widgetId/${id}/submenu`
      );
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * Fetches VOD submenu
   * @param {number|string} id - Widget ID
   * @param {number|string} bid - Building ID
   * @returns {Promise<Object>} - VOD submenu data
   */
  static async fetchVodSubMenu(id, bid) {
    try {
      const response = await this.get(
        `${process.env.API_PORTAL}/bid/${bid}/vod/`
      );
      console.log("===================================fetchVodSubMenu", id, response);
      return response;
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * Fetches all EPG data
   * @returns {Promise<Object>} - EPG data
   */
  static async fetchAllEPGData() {
    try {
      return await this.get(`${process.env.EPG_API}/channels`);
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * Fetches EPG data for a specific channel
   * @param {number|string} id - Channel ID
   * @returns {Promise<Object>} - EPG data for channel
   */
  static async fetchEPGDataById(id) {
    try {
      return await this.get(`${process.env.EPG_API}/channel/${id}`);
    } catch (err) {
      return Promise.reject(err);
    }
  }
}



// WEBPACK FOOTER //
// ./src/helpers/Request.js